/*
 * This component is used by both Site List and Site Summary Screens
 */
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormControl } from '@angular/forms';
import { DataCollection } from '../../models/site-content/data-collection.model';
import { Observable } from 'rxjs';
import { map, startWith, debounceTime, switchMap } from 'rxjs/operators';
import { MaxPickerService } from '../../services/max-picker.service';
import { PickerItem } from '../../models/picker-item.model';
import { Logging } from '../../logging';
import { DrawerComponentBase } from '../drawer-component-base/drawer-component-base.component';
import { IDrawerComponentLiaison } from '../../interfaces/idrawer-component-liaison';
import { DataCollectionService } from '../../services/data-collection.service';
import { CurrentSiteService } from '../../services/current-site.service';

// TODO - get rid of this. It's MOCK data for AutoComplete 
export interface User {
    id: string;
    name: string;
}

@Component({
    selector: 'app-site-editor',
    templateUrl: './site-editor.component.html',
    styleUrls: ['./site-editor.component.scss', '../properties.scss'],
    standalone: false
})
export class SiteEditorComponent extends DrawerComponentBase implements OnInit {
    @Input() drawerComponentLiaison: IDrawerComponentLiaison = null;
    @Input() display: string;
    @Input() showTitle: boolean = true;
    @Input() site: DataCollection;

    @Output() siteSaved: EventEmitter<DataCollection> = new EventEmitter();
    @Output() editCanceled: EventEmitter<boolean> = new EventEmitter();

    form: UntypedFormGroup;
    pocPickerItems: Observable<PickerItem[]>; // observable of PickerItems which will be observed in the template via use of the async pipe

    pocDisplayName: string;
    showLoadingIndicator: boolean;
    timeoutId: any;

    // TODO - replace with db lookup 
    frequencyOfCollection: string[] = ['Annual', 'One-Time', 'Ongoing'];

    constructor(
        private fb: UntypedFormBuilder,
        private maxPickerService: MaxPickerService,
        private siteService: DataCollectionService,
        private currentSiteService: CurrentSiteService) {
        super();
    }

    ngOnInit() {
        let s = this.site;
        this.pocDisplayName = this.site.maxPointOfContactDspl;
        this.buildForm(s);
        this.initializePOCPickerItems();
        this.initializeLoadingIndicator();
    }

    public save() {
        this.updateSiteFromForm();

        this.siteService.update(this.site).then(site => {
            this.siteSaved.emit(site);
            if (this.currentSiteService.Site.id == site.id) {
                this.currentSiteService.Site = site;
            }
        });
    }

    public reset() {
        this.form.reset(this.site);
    }

    public cancel() {
        this.editCanceled.emit();
    }

    public formIsDirty() {
        return this.form.dirty;
    }

    public showDetails(): boolean {
        return this.display === 'details';
    }

    public showContacts(): boolean {
        return this.display === 'contacts';
    }

    public pickerDisplayFn(pi: PickerItem) {
        if (this.pocDisplayName != null) {
            return this.pocDisplayName;
        } else {
            let displayName: any = pi?.displayName;
            if (typeof displayName === 'string') {
                return displayName;
            }
            else {
                return '';
            }
        }
    }

    // Form some reason, the built-in `Validators.required` for the maxPointOfContact field (see below)
    // is not failing validation when no poc is set, so I had to add this
    // Currently however, not requiring this field
    public maxPointOfContactHasValue(): boolean {
        let val = this.form.value.maxPointOfContact?.displayName;
        return val;
    }

    private buildForm(s: DataCollection) {
        this.form = this.fb.group({
            id: [s.id],
            name: [{ value: s.name, disabled: !s.siteIsBuildable }, Validators.required],
            purpose: [{ value: s.purpose, disabled: !s.siteIsBuildable }],
            frequency: [{ value: s.frequency, disabled: !s.siteIsBuildable }],
            description: [{ value: s.description, disabled: !s.siteIsBuildable }],
            maxPointOfContact: [{ displayName: s.maxPointOfContactDspl, disabled: !s.siteIsBuildable }],
            startDate: [s.startDate],
            endDate: [s.endDate]
        });

    }

    private initializePOCPickerItems(): void {
        this.pocPickerItems = this.form.controls['maxPointOfContact'].valueChanges.pipe(
            debounceTime(300),                                          // swallow multiple valueChanges made within 300ms period and just keep the most recent one
            switchMap(value => this.maxPickerService.search(value)) // switch to a new observable, canceling the previous
        );
    }

    private initializeLoadingIndicator(): void {
        this.maxPickerService.isSearching.subscribe((value) => {
            if (value) {
                if (!this.showLoadingIndicator) {
                    this.timeoutId = setTimeout(() => { this.showLoadingIndicator = true }, 450);
                }
            }
            else {
                clearTimeout(this.timeoutId);
                this.showLoadingIndicator = false;
            }
        });
    }

    private updateSiteFromForm() {
        let savedPoc = this.site.maxPointOfContact;
        this.site = this.form.value;

        let submittedPoc = this.form.value.maxPointOfContact;

        if (submittedPoc?.key) { // a new poc has been picked
            this.site.maxPointOfContact = submittedPoc.key;
        } else if (submittedPoc?.displayName) { // no new poc has been picked, but we have the name of the eisting one 
            this.site.maxPointOfContact = savedPoc;
        } else { // no poc has been defined
            this.site.maxPointOfContact = null;
            this.site.maxPointOfContactDspl = null;
            this.site.maxPointOfContactEmail = null;
        }
    }
}
